import { DoNotDisturbOn, PauseCircle } from '@mui/icons-material';
import moment from 'moment';
import { tz } from 'moment-timezone';

import { IOrderFrequency, IOrderPrn, isOrderDiscontinued } from '../../types/order';
import { ClickEvent } from '../../utils/clickEvent';
import { formatNullableDatetime } from '../../utils/datetimes/formatting';
import { AxleChip } from '../displayFields/chips';
import { OrderChipFragment } from './OrderChip.fragments';
import { OrderFrequencyChip } from './OrderFrequencyChip';
import { renderOrderFrequency1xStyle } from './labels';

export { OrderChipFragment };

export interface IOrderChip {
    id: string;
    externalId: string | null;
    prn: IOrderPrn | null;
    frequency: IOrderFrequency | null;
    discontinuedAt: Date | null;
    isPaused: boolean;
    timezone: string | null;
    permissions: {
        viewDetails: boolean;
    };
}

export const OrderChip = ({
    order: { id, frequency, prn, discontinuedAt, timezone, isPaused, permissions },
}: {
    order: IOrderChip;
}) => {
    const isDiscontinued = isOrderDiscontinued(discontinuedAt);
    const tooltip = isDiscontinued
        ? `Discontinued ${formatNullableDatetime(moment(discontinuedAt), timezone ?? tz.guess())}`
        : '';
    const linkUrl = permissions.viewDetails ? `/orders/${id}` : undefined;
    const onClick = permissions.viewDetails
        ? (event: ClickEvent) => {
              // Stop propagation so that table row click events don't trigger..
              event.stopPropagation();
          }
        : undefined;
    if (prn) {
        return (
            <AxleChip
                label='PRN Visits'
                tooltip={tooltip}
                numberIcon={isDiscontinued ? undefined : prn.authorizedVisitCount}
                icon={isDiscontinued ? <DoNotDisturbOn /> : undefined}
                routerLink={linkUrl}
                onClick={onClick}
            />
        );
    }
    if (frequency) {
        const label = renderOrderFrequency1xStyle({ frequency, withIntervals: true });
        if (isDiscontinued) {
            return (
                <AxleChip
                    icon={<DoNotDisturbOn />}
                    label={label}
                    tooltip={tooltip}
                    routerLink={linkUrl}
                    onClick={onClick}
                />
            );
        }
        if (isPaused) {
            return (
                <AxleChip
                    icon={<PauseCircle />}
                    label={label}
                    tooltip={tooltip}
                    routerLink={linkUrl}
                    onClick={onClick}
                />
            );
        }
        return (
            <OrderFrequencyChip
                frequency={frequency}
                tooltip={tooltip}
                routerLink={linkUrl}
                onClick={onClick}
            />
        );
    }
    return <AxleChip label='Order' tooltip={tooltip} />;
};
